import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Building2, Plus, BarChart3, ChevronRight, LogOut, Calendar } from 'lucide-react';
import { sponsorApi } from '@/lib/api';
import { toast } from 'sonner';
import { isAuthenticated, getStoredUser, clearAuthData, formatDate } from '@/lib/utils';

export default function SponsorDashboard() {
  const navigate = useNavigate();
  const [sponsor, setSponsor] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  
  // Create campaign form
  const [campaignName, setCampaignName] = useState('');
  const [artifactType, setArtifactType] = useState('notes');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/sponsor');
      return;
    }
    
    const user = getStoredUser();
    if (user?.user_type !== 'sponsor') {
      navigate('/sponsor');
      return;
    }
    
    setSponsor(user);
    loadCampaigns();
  }, [navigate]);

  const loadCampaigns = async () => {
    try {
      const response = await sponsorApi.getCampaigns();
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    clearAuthData();
    navigate('/sponsor');
    toast.success('Logged out');
  };

  const handleCreateCampaign = async () => {
    if (!campaignName) return;
    
    setIsCreating(true);
    try {
      await sponsorApi.createCampaign(campaignName, artifactType, new Date().toISOString(), null);
      toast.success('Campaign created!');
      setShowCreateCampaign(false);
      setCampaignName('');
      loadCampaigns();
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sponsor-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-slate-900">{sponsor?.company_name || 'Sponsor Portal'}</h1>
              <p className="text-xs text-slate-500">{sponsor?.email}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => setShowCreateCampaign(true)}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              data-testid="sponsor-logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Your Campaigns</h2>
          <p className="text-slate-600 mb-8">Manage and track your MEMORA campaigns</p>

          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-slate-100 rounded w-1/2 mb-6" />
                  <div className="h-20 bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          ) : campaigns.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-slate-100">
              <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 className="w-10 h-10 text-amber-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No campaigns yet</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Create your first campaign to start distributing MEMORA artifacts and tracking engagement.
              </p>
              <Button 
                onClick={() => setShowCreateCampaign(true)}
                className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-8"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campaigns.map((campaign, index) => (
                <motion.div
                  key={campaign.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/sponsor/campaign/${campaign.id}`} data-testid={`campaign-card-${campaign.id}`}>
                    <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-amber-200 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-1">{campaign.name}</h3>
                          <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full capitalize">
                            {campaign.artifact_type}
                          </span>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-amber-500 group-hover:translate-x-1 transition-all" />
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Calendar className="w-4 h-4" />
                        <span>Started {formatDate(campaign.start_date)}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>

      {/* Create Campaign Dialog */}
      <Dialog open={showCreateCampaign} onOpenChange={setShowCreateCampaign}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Campaign</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Campaign Name</label>
              <Input
                placeholder="Toyota Smart Notebook 2026"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="campaign-name-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Artifact Type</label>
              <Select value={artifactType} onValueChange={setArtifactType}>
                <SelectTrigger className="rounded-xl" data-testid="artifact-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="notes">Memora Notes</SelectItem>
                  <SelectItem value="travel">Memora Travel Tag</SelectItem>
                  <SelectItem value="home">Memora Home Hub</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={handleCreateCampaign}
              disabled={isCreating || !campaignName}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              data-testid="confirm-campaign-btn"
            >
              {isCreating ? 'Creating...' : 'Create Campaign'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
